import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, Trash2, Grid3x3 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Loading } from '../../components/Loading';
import { ImageUploader } from '../../components/ImageUploader';
import { getCategories, addCategory, updateCategory, deleteCategory } from '../../utils/firestoreService';

export const AdminCategories = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error al cargar las categorías');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Debes estar autenticado');
      return;
    }

    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, formData);
        toast.success('Categoría actualizada correctamente');
      } else {
        await addCategory(formData);
        toast.success('Categoría creada correctamente');
      }

      setDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Error al guardar la categoría. Verifica las reglas de Firestore.');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!user) {
      toast.error('Debes estar autenticado');
      return;
    }

    if (!window.confirm('¿Estás seguro de eliminar esta categoría?')) return;

    try {
      await deleteCategory(categoryId);
      toast.success('Categoría eliminada correctamente');
      fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Error al eliminar la categoría');
    }
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description,
      imageUrl: category.imageUrl || '',
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      imageUrl: '',
    });
  };

  if (loading) return <Loading />;

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-4xl font-fredoka font-bold text-[#1A1A2E]" data-testid="admin-categories-title">
          Categorías
        </h1>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <button
              className="bg-[#00C2CB] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#00C2CB]/90 transition-all flex items-center gap-2"
              data-testid="add-category-button"
            >
              <Plus className="w-5 h-5" />
              Nueva Categoría
            </button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-fredoka text-2xl">
                {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <Label htmlFor="description">Descripción *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                  data-testid="category-description-input"
                />
              </div>
              <div>
                <Label htmlFor="imageUrl">Imagen de la Categoría *</Label>
                <ImageUploader
                  currentImage={formData.imageUrl}
                  onImageUploaded={(url) => setFormData({ ...formData, imageUrl: url })}
                />
                <p className="text-xs text-[#9CA3AF] mt-2 font-quicksand">
                  La imagen se subirá a Firebase Storage
                </p>
              </div>
              <button
                type="submit"
                className="w-full bg-[#FF6B00] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#FF6B00]/90 transition-all"
                data-testid="category-submit-button"
              >
                {editingCategory ? 'Actualizar' : 'Crear'} Categoría
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.length === 0 && !loading ? (
          <div className="col-span-full text-center py-20 px-4 bg-white rounded-2xl shadow-lg">
            <Grid3x3 className="w-16 h-16 mx-auto mb-4 text-[#9CA3AF]" />
            <h3 className="text-xl font-fredoka font-bold text-[#1A1A2E] mb-2">
              No hay categorías aún
            </h3>
            <p className="text-[#4A4A6A] font-quicksand mb-6">
              Crea categorías para organizar tus productos. Por ejemplo: Bebés, Niños, Niñas, Didácticos, Peluches.
            </p>
            <button
              onClick={() => setDialogOpen(true)}
              className="bg-[#00C2CB] text-white px-6 py-3 rounded-full font-fredoka font-bold hover:bg-[#00C2CB]/90 transition-all"
              data-testid="empty-state-add-category"
            >
              Crear Primera Categoría
            </button>
          </div>
        ) : (
          categories.map((category) => (
          <div
            key={category.id}
            className="bg-white rounded-2xl p-6 shadow-lg"
            data-testid={`category-card-${category.id}`}
          >
            <div className="mb-4">
              {category.imageUrl ? (
                <img src={category.imageUrl} alt={category.name} className="w-full h-40 object-cover rounded-xl" />
              ) : (
                <div className="w-full h-40 bg-slate-100 rounded-xl flex items-center justify-center">
                  <span className="text-slate-400 font-quicksand">Sin imagen</span>
                </div>
              )}
            </div>
            <h3 className="font-fredoka font-bold text-xl text-[#1A1A2E] mb-2">{category.name}</h3>
            <p className="text-[#4A4A6A] font-quicksand mb-4 line-clamp-2">{category.description}</p>
            <div className="flex gap-2">
              <button
                onClick={() => openEditDialog(category)}
                className="flex-1 px-4 py-2 text-[#00C2CB] border border-[#00C2CB] hover:bg-[#00C2CB]/10 rounded-full font-quicksand font-semibold transition-all"
                data-testid={`edit-category-${category.id}`}
              >
                <Edit2 className="inline-block w-4 h-4 mr-2" />
                Editar
              </button>
              <button
                onClick={() => handleDelete(category.id)}
                className="flex-1 px-4 py-2 text-red-600 border border-red-600 hover:bg-red-50 rounded-full font-quicksand font-semibold transition-all"
                data-testid={`delete-category-${category.id}`}
              >
                <Trash2 className="inline-block w-4 h-4 mr-2" />
                Eliminar
              </button>
            </div>
          </div>
        )))}
      </div>
    </div>
  );
};